// ./useTokioCoverage.ts

import { useState, useEffect, useCallback, RefObject } from "react";
import type { FormHandles } from "@unform/core";
import { globalRemoveMaskReal2 } from "../../../../../shared/utils";

interface UseTokioCoverageResult {
	coverageType: string;
	setCoverageType: React.Dispatch<React.SetStateAction<string>>;
	showOutras: boolean;
	handlePredialBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
}

export const useTokioCoverage = (formRef: RefObject<FormHandles>): UseTokioCoverageResult => {
	const [coverageType, setCoverageType] = useState<string>("Predio_e_Conteudo_90_10");
	const showOutras = coverageType === "Predio_e_Conteudo_Outras";
	const toRealRaw = (x: number) => String(Math.round(x * 100));

	const presetFromCoverage = (ct: string): number | null => {
		if (ct === "Predio_e_Conteudo_90_10") return 90;
		if (ct === "Predio_e_Conteudo_80_20") return 80;
		if (ct === "Predio_e_Conteudo_70_30") return 70;
		return null;
	};

	const setPredioEConteudoFromNumber = useCallback(
		(predioPercent: number) => {
			const p = Math.min(100, Math.max(0, Number(predioPercent) || 0));
			const c = Number((100 - p).toFixed(2));
			formRef.current?.setFieldValue("pc_predio", toRealRaw(p));
			formRef.current?.setFieldValue("pc_conteudo", toRealRaw(c));
		},
		[formRef]
	);

	const setPredioEConteudoFromInput = useCallback(
		(predioRaw: string | number) => {
			const n = Number(globalRemoveMaskReal2(String(predioRaw ?? "0")));
			const p = isNaN(n) ? 0 : Math.min(100, Math.max(0, n));
			setPredioEConteudoFromNumber(p);
		},
		[setPredioEConteudoFromNumber]
	);

	// Handler para o onBlur do campo PredialPercent
	const handlePredialBlur = useCallback(
		(e: React.FocusEvent<HTMLInputElement>) => {
			setPredioEConteudoFromInput(e.target.value);
		},
		[setPredioEConteudoFromInput]
	);

	// EFEITO 1: Sincroniza o estado interno do hook com o valor inicial do formulário (carregado via useTokioData)
	useEffect(() => {
		if (!formRef.current) return;
		// O campo coverageType não teve seu nome alterado, então a leitura está OK
		const initialFormValue = formRef.current.getFieldValue?.("coverageType");
		if (initialFormValue && initialFormValue !== coverageType) {
			setCoverageType(initialFormValue);
		}
	}, [formRef]);

	// EFEITO 2: Aplica os percentuais quando o tipo de cobertura muda
	useEffect(() => {
		const preset = presetFromCoverage(coverageType);

		if (preset !== null) {
			setPredioEConteudoFromNumber(preset);
		} else if (coverageType === "Predio_e_Conteudo_Outras") {
			const currentRaw = formRef.current?.getFieldValue?.("pc_predio");
			const currentNumericValue = Number(globalRemoveMaskReal2(String(currentRaw ?? "0")));

			if (currentNumericValue === 0) {
				setPredioEConteudoFromNumber(50); // Aplica o default 50/50
			}
		}
	}, [coverageType, formRef, setPredioEConteudoFromNumber]);

	return {
		coverageType,
		setCoverageType,
		showOutras,
		handlePredialBlur,
	};
};
